import { ComponentRef, Injectable, Type } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MobileDialogComponent } from './mobile-dialog.component';
import { BehaviorSubject, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import {
	bindVisualViewportChange,
	getStableViewportHeightCss,
	getVisualViewportMetrics,
	lockBodyScroll,
	measureSmallViewportHeight,
	needsIOSViewportWorkaround,
} from './visual-viewport.util';

interface MobileDialogOptions<F = unknown> {
	panelClass?: string | string[];
	footerComponent?: Type<F>;
	footerData?: Record<string, unknown>;
}

export interface MobileDialogRefs<C> {
	content: ComponentRef<C>;
}

const VIEWPORT_PANEL_CLASS = 'mobile-dialog-viewport-panel';

@Injectable({
	providedIn: 'root',
})
export class MobileDialogService {
	constructor(private readonly dialog: MatDialog) {}

	afterClose$ = new Subject<void>();
	shouldScroll$ = new BehaviorSubject<boolean>(false);

	openDialogWithComponent<C = unknown, T = unknown, F = unknown>(
		component: Type<C>,
		data: { title?: string } & T,
		options: MobileDialogOptions<F> = {},
	): MobileDialogRefs<C> {
		const condition = document.body.clientHeight > 750;
		const margin = condition ? 4 : 0;
		const reservedTop = condition ? 60 : 0;
		const ios = needsIOSViewportWorkaround();
		const box = this.resolveDialogBox(reservedTop, margin);
		const height = ios ? getStableViewportHeightCss(reservedTop, margin) : `${box.height}px`;

		const dialogRef = this.dialog.open(MobileDialogComponent, {
			width: `calc(100vw - ${2 * margin}px)`,
			maxWidth: `calc(100vw - ${2 * margin}px)`,
			height,
			maxHeight: height,
			position: {
				top: `${ios ? reservedTop : box.top}px`,
			},
			autoFocus: false,
			panelClass: this.mergePanelClass(options.panelClass),
		});

		dialogRef.componentRef.setInput('title', data.title);
		dialogRef.componentRef.setInput('hasFooter', !!options.footerComponent);
		dialogRef.componentRef.changeDetectorRef.detectChanges();

		const dialogInstance = dialogRef.componentInstance;
		const contentRef = dialogInstance.viewContainerRef().createComponent(component);

		for (const key in data) {
			if (key === 'title') {
				continue;
			}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			contentRef.setInput(key, (data as any)[key]);
		}

		this.startViewportSync(dialogRef, reservedTop, margin, ios);

		if (options.footerComponent) {
			dialogRef
				.afterOpened()
				.pipe(take(1))
				.subscribe(() => {
					dialogInstance.attachFooter(options.footerComponent!, options.footerData ?? {});
				});
		}

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(() => {
				this.afterClose$.next();
			});

		return { content: contentRef };
	}

	close(): void {
		this.dialog.closeAll();
	}

	private mergePanelClass(panelClass?: string | string[]): string[] {
		const extra = !panelClass ? [] : Array.isArray(panelClass) ? panelClass : [panelClass];
		return [VIEWPORT_PANEL_CLASS, ...extra];
	}

	private resolveDialogBox(
		reservedTop: number,
		margin: number,
	): {
		height: number;
		top: number;
	} {
		const { height: viewportHeight, top: viewportTop } = getVisualViewportMetrics();
		return {
			height: Math.max(0, viewportHeight - reservedTop - margin),
			top: viewportTop + reservedTop,
		};
	}

	private startViewportSync(
		dialogRef: MatDialogRef<MobileDialogComponent>,
		reservedTop: number,
		margin: number,
		ios: boolean,
	): void {
		let unbindViewport: (() => void) | null = null;
		let unlockBody: (() => void) | null = null;

		const sync = (): void => {
			const pane = document.querySelector(`.cdk-overlay-pane.${VIEWPORT_PANEL_CLASS}`) as HTMLElement | null;
			if (!pane) {
				return;
			}

			if (ios) {
				const small = measureSmallViewportHeight();
				const vv = window.visualViewport?.height ?? window.innerHeight;
				const reserved = reservedTop + margin;
				// Keyboard open: visual viewport clearly smaller than svh → clamp in px.
				const keyboardOpen = vv < small - 24;
				const height = keyboardOpen
					? `${Math.max(0, Math.round(vv) - reserved)}px`
					: getStableViewportHeightCss(reservedTop, margin);

				dialogRef.updateSize(`calc(100vw - ${2 * margin}px)`, height);
				dialogRef.updatePosition({ top: `${reservedTop}px` });
				pane.style.setProperty(
					'--mobile-dialog-height',
					keyboardOpen ? `${Math.max(0, Math.round(vv) - reserved)}px` : '100svh',
				);
				pane.style.setProperty('--mobile-dialog-top', `${reservedTop}px`);
				return;
			}

			const { height, top } = this.resolveDialogBox(reservedTop, margin);
			dialogRef.updateSize(`calc(100vw - ${2 * margin}px)`, `${height}px`);
			dialogRef.updatePosition({ top: `${top}px` });
			pane.style.setProperty('--mobile-dialog-height', `${height}px`);
			pane.style.setProperty('--mobile-dialog-top', `${top}px`);
		};

		dialogRef
			.afterOpened()
			.pipe(take(1))
			.subscribe(() => {
				unlockBody = lockBodyScroll();
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						sync();
						unbindViewport = bindVisualViewportChange(sync);
					});
				});
			});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(() => {
				unbindViewport?.();
				unlockBody?.();
			});
	}
}
